import { YES_NO, applicantSection, feeInfo, otherAttachments, penaltyDeclaration, replySection } from "modules/ebiForms/common";
import {
  FOREIGNER,
  abroadFields,
  bankAccountField,
  citizenshipFields,
  familyIncomeAttachments,
  familyIncomeFields,
  personFields,
  personsField,
  socialProxyAttachment,
  socialRepresentationSection,
} from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiField, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C6. Wniosek o ustalenie prawa do świadczeń rodzinnych — art. 23 ustawy z 28.11.2003 r. o świadczeniach rodzinnych;
 * rozporządzenie MRiPS z 6.07.2023 r. (Dz.U. poz. 1340). Jeden kod, wariant = świadczenie (tytuł przesyłki = nazwa
 * świadczenia ze wzoru): zasiłek rodzinny z dodatkami (SR-1), becikowe (SR-2), zasiłek pielęgnacyjny (SR-3), specjalny
 * zasiłek opiekuńczy — tylko prawa nabyte do 31.12.2023 (SR-4), świadczenie pielęgnacyjne od 1.01.2024 (NSR-5),
 * świadczenie rodzicielskie (SR-7). Układ pól i brzmienie oświadczeń według wzorów MRPiPS pobranych 22.10.2026 z gov.pl
 * (SR-1 https://www.gov.pl/attachment/49f70c4e-ca14-44a1-9c2f-35e0216e4a2d, SR-2 …/20ec64a0-3df1-49dc-9884-369596f3818e,
 * SR-3 …/4cc3267f-5745-4dcf-aa6e-ab6b79782996, SR-4 …/46c063c3-4d2e-447a-b9dd-5846207c6d92,
 * NSR-5 …/d0ff4707-26d0-49c9-80e5-9669b997fe0e, SR-7 …/e0953398-43da-49f9-b8f5-a766bb93be81). Pominięto części
 * „organ właściwy” (to urząd z EBI) i dane z mObywatela; dzieci i członków rodziny wpisuje się raz na listach.
 * Oświadczenia składane na osobnych drukach (ZSR-05, ZSR-07, ZSR-08, ZSR-12) są załącznikami sterowanymi pytaniami;
 * oświadczenie o niekorzystaniu z placówki całodobowej (ZSR-10) zastępuje oświadczenie w formularzu (jak w SR-1 cz. II).
 */
const SW = (value: string): EbiCondition => ({ field: "swiadczenie", equals: value });

const ZR = SW("zasilekRodzinny");
const BEC = SW("becikowe");
const ZP = SW("zasilekPielegnacyjny");
const SZO = SW("specjalnyZasilekOpiekunczy");
const SP = SW("swiadczeniePielegnacyjne");
const SRD = SW("swiadczenieRodzicielskie");
const INCOME_TESTED: EbiCondition = { field: "swiadczenie", in: ["zasilekRodzinny", "becikowe", "specjalnyZasilekOpiekunczy"] };
const CHILD_SUPPORT: EbiCondition = { all: [{ any: [ZR, BEC] }, { field: "alimentyNaDzieci", equals: "tak" }] };

const dodatek = (value: string): EbiCondition => ({ all: [ZR, { field: "dodatki", includes: value }] });

/** Dodatki do zasiłku rodzinnego (SR-1, cz. I pkt 2). */
const ALLOWANCES = [
  { value: "urodzenie", label: "urodzenia dziecka", short: "urodzenia dziecka" },
  { value: "urlop", label: "opieki nad dzieckiem w okresie korzystania z urlopu wychowawczego", short: "urlopu wychowawczego" },
  { value: "samotne", label: "samotnego wychowywania dziecka (najwyżej na dwoje dzieci)", short: "samotnego wychowywania" },
  {
    value: "wielodzietna",
    label: "wychowywania dziecka w rodzinie wielodzietnej (na trzecie i kolejne dziecko uprawnione do zasiłku rodzinnego)",
    short: "rodziny wielodzietnej",
  },
  { value: "niepelnosprawnosc", label: "kształcenia i rehabilitacji dziecka niepełnosprawnego", short: "kształcenia i rehabilitacji" },
  { value: "rokSzkolny", label: "rozpoczęcia roku szkolnego", short: "rozpoczęcia roku szkolnego" },
  {
    value: "naukaZamieszkanie",
    label: "podjęcia przez dziecko nauki w szkole poza miejscem zamieszkania — zamieszkanie w miejscowości, w której jest szkoła",
    short: "nauki poza miejscem zamieszkania (zamieszkanie)",
  },
  {
    value: "naukaDojazd",
    label: "podjęcia przez dziecko nauki w szkole poza miejscem zamieszkania — dojazd do miejscowości, w której jest szkoła",
    short: "nauki poza miejscem zamieszkania (dojazd)",
  },
];

/** Dziecko na liście: dane osoby bez pokrewieństwa (+ pola wariantu). */
const childFields = (...extra: IEbiField[]): IEbiField[] => [...personFields().filter((f) => f.code !== "pokrewienstwo"), ...extra];

const personOfCare = (prefix: string, visibleWhen?: EbiCondition): IEbiField[] => [
  { code: `${prefix}Imie`, type: "text", label: "Imię", required: true, visibleWhen, width: "half", maxLength: 100 },
  { code: `${prefix}Nazwisko`, type: "text", label: "Nazwisko", required: true, visibleWhen, width: "half", maxLength: 100 },
  { code: `${prefix}Pesel`, type: "pesel", label: "PESEL", required: true, visibleWhen, width: "half" },
  { code: `${prefix}Adres`, type: "text", label: "Adres zamieszkania (jeśli inny niż wnioskodawcy)", visibleWhen, maxLength: 300 },
];

const C6: IEbiFormDefinition = {
  code: "C6",
  version: 1,
  title: "Wniosek o ustalenie prawa do świadczeń rodzinnych",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description:
    "Zasiłek rodzinny z dodatkami, becikowe, zasiłek pielęgnacyjny, świadczenie pielęgnacyjne, specjalny zasiłek opiekuńczy i świadczenie rodzicielskie.",
  keywords: [
    "świadczenia rodzinne",
    "zasiłek rodzinny",
    "dodatki",
    "becikowe",
    "zasiłek pielęgnacyjny",
    "świadczenie pielęgnacyjne",
    "specjalny zasiłek opiekuńczy",
    "świadczenie rodzicielskie",
    "SR-1",
  ],
  organ: "Wójt / Burmistrz / Prezydent Miasta właściwy według miejsca zamieszkania wnioskodawcy — realizuje ośrodek pomocy społecznej lub centrum świadczeń",
  legalBasis:
    "art. 23 ustawy z dnia 28 listopada 2003 r. o świadczeniach rodzinnych; rozporządzenie Ministra Rodziny i Polityki Społecznej z dnia 6 lipca 2023 r. w sprawie sposobu i trybu postępowania w sprawach o przyznanie świadczeń rodzinnych",
  request: "Wnoszę o ustalenie prawa do świadczenia rodzinnego wskazanego we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (art. 2 ust. 1 pkt 1 lit. c ustawy o opłacie skarbowej).",
  variantField: "swiadczenie",
  variants: {
    zasilekRodzinny: { title: "Wniosek o ustalenie prawa do zasiłku rodzinnego oraz dodatków do zasiłku rodzinnego" },
    becikowe: { title: "Wniosek o ustalenie prawa do jednorazowej zapomogi z tytułu urodzenia się dziecka" },
    zasilekPielegnacyjny: { title: "Wniosek o ustalenie prawa do zasiłku pielęgnacyjnego" },
    specjalnyZasilekOpiekunczy: { title: "Wniosek o ustalenie prawa do specjalnego zasiłku opiekuńczego" },
    swiadczeniePielegnacyjne: { title: "Wniosek o ustalenie prawa do świadczenia pielęgnacyjnego" },
    swiadczenieRodzicielskie: { title: "Wniosek o ustalenie prawa do świadczenia rodzicielskiego" },
  },
  sections: [
    {
      code: "rodzajSwiadczenia",
      title: "Rodzaj świadczenia",
      fields: [
        {
          code: "swiadczenie",
          type: "radio",
          label: "Wnoszę o ustalenie prawa do",
          required: true,
          options: [
            { value: "zasilekRodzinny", label: "zasiłku rodzinnego oraz dodatków do zasiłku rodzinnego (wzór SR-1)" },
            { value: "becikowe", label: "jednorazowej zapomogi z tytułu urodzenia się dziecka — „becikowego” (wzór SR-2)" },
            { value: "zasilekPielegnacyjny", label: "zasiłku pielęgnacyjnego (wzór SR-3)" },
            {
              value: "specjalnyZasilekOpiekunczy",
              label: "specjalnego zasiłku opiekuńczego — na zasadach obowiązujących do 31 grudnia 2023 r. (wzór SR-4)",
            },
            { value: "swiadczeniePielegnacyjne", label: "świadczenia pielęgnacyjnego — na zasadach obowiązujących od 1 stycznia 2024 r. (wzór NSR-5)" },
            { value: "swiadczenieRodzicielskie", label: "świadczenia rodzicielskiego (wzór SR-7)" },
          ],
        },
        feeInfo(
          "infoEmpatia",
          "Wniosek o świadczenia rodzinne możesz złożyć także przez portal Emp@tia (wnioski.empatia.gov.pl). Oświadczenia składane na osobnych drukach (np. ZSR-05, ZSR-07) udostępnia urząd i serwis gov.pl — w tym formularzu dołączasz je jako załączniki.",
        ),
        {
          ...feeInfo(
            "infoSzo",
            "Specjalny zasiłek opiekuńczy ustala się na zasadach obowiązujących do 31 grudnia 2023 r. Zasiłek nie przysługuje, jeżeli osoba wymagająca opieki ma przyznane prawo do świadczenia wspierającego (art. 63 ust. 6 ustawy z dnia 7 lipca 2023 r. o świadczeniu wspierającym).",
          ),
          visibleWhen: SZO,
        },
        {
          code: "okresZasilkowy",
          type: "text",
          label: "Wniosek dotyczy okresu zasiłkowego",
          required: true,
          visibleWhen: { any: [ZR, SZO] },
          width: "third",
          maxLength: 9,
          placeholder: "2026/2027",
          hint: "Okres zasiłkowy trwa od 1 listopada do 31 października następnego roku.",
        },
      ],
    },
    applicantSection(),
    {
      code: "daneWnioskodawcy",
      title: "Obywatelstwo i stan cywilny",
      fields: citizenshipFields(INCOME_TESTED),
    },
    socialRepresentationSection(),
    {
      code: "zasilekRodzinny",
      title: "Zasiłek rodzinny i dodatki",
      visibleWhen: ZR,
      fields: [
        personsField({
          code: "dzieciZasilek",
          label: "Dzieci, na które składasz wniosek o zasiłek rodzinny",
          itemLabel: "Dziecko",
          required: true,
          fields: childFields({
            code: "szkola",
            type: "text",
            label: "Szkoła lub szkoła wyższa, do której uczęszcza dziecko (rodzaj i adres)",
            maxLength: 300,
            hint: "Dla dziecka pełnoletniego albo kończącego 18 lat w okresie zasiłkowym. Na dziecko uczące się w szkole wyższej zasiłek przysługuje (do 24. roku życia) tylko przy orzeczeniu o umiarkowanym albo znacznym stopniu niepełnosprawności.",
          }),
        }),
        {
          code: "dodatki",
          type: "checkboxes",
          label: "Wnoszę także o dodatki do zasiłku rodzinnego z tytułu",
          options: ALLOWANCES.map(({ value, label }) => ({ value, label })),
        },
        ...ALLOWANCES.map(
          ({ value, short }): IEbiField => ({
            code: `${value}Dzieci`,
            type: "text",
            label: `Dodatek z tytułu ${short} — imię i nazwisko dziecka (dzieci)`,
            required: true,
            visibleWhen: dodatek(value),
            maxLength: 300,
          }),
        ),
        {
          code: "urlopPobierany",
          type: "radio",
          label: "Dodatek z tytułu opieki nad dzieckiem w okresie korzystania z urlopu wychowawczego na to dziecko (dzieci)",
          required: true,
          visibleWhen: dodatek("urlop"),
          options: [
            { value: "nigdy", label: "nie był nigdy pobierany" },
            { value: "pobierany", label: "był pobierany" },
          ],
        },
        {
          code: "urlopOkres",
          type: "text",
          label: "Okres pobierania dodatku (od dnia — do dnia)",
          required: true,
          visibleWhen: { all: [dodatek("urlop"), { field: "urlopPobierany", equals: "pobierany" }] },
          maxLength: 200,
        },
        {
          code: "oswiadczenieUrlop",
          type: "declaration",
          label:
            "Oświadczam, że: z dodatku do zasiłku rodzinnego z tytułu opieki nad dzieckiem w okresie korzystania z urlopu wychowawczego na to samo dziecko nie korzysta drugi z rodziców ani opiekun prawny; nie otrzymuję zasiłku macierzyńskiego ani świadczenia rodzicielskiego; nie podjęłam/nie podjąłem ani nie kontynuuję zatrudnienia lub innej pracy zarobkowej, która uniemożliwia mi sprawowanie osobistej opieki nad dzieckiem; dziecko nie zostało umieszczone w placówce zapewniającej całodobową opiekę, w tym w specjalnym ośrodku szkolno-wychowawczym (z wyjątkiem podmiotu wykonującego działalność leczniczą), ani nie korzysta w niej z całodobowej opieki przez więcej niż 5 dni w tygodniu.",
          required: true,
          visibleWhen: dodatek("urlop"),
        },
        {
          code: "wspolneWychowanie",
          type: "radio",
          label: "Wspólnie z drugim rodzicem",
          required: true,
          visibleWhen: dodatek("samotne"),
          options: [
            { value: "nie", label: "nie wychowuję co najmniej jednego dziecka" },
            { value: "tak", label: "wychowuję co najmniej jedno dziecko" },
          ],
        },
        {
          code: "alimentyNiezasadzone",
          type: "radio",
          label: "Świadczenie alimentacyjne na rzecz dziecka od drugiego z rodziców nie zostało zasądzone, ponieważ",
          required: true,
          visibleWhen: dodatek("samotne"),
          options: [
            { value: "zmarly", label: "drugi z rodziców dziecka nie żyje" },
            { value: "ojciecNieznany", label: "ojciec dziecka jest nieznany" },
            { value: "oddalone", label: "powództwo o ustalenie świadczenia alimentacyjnego od drugiego z rodziców zostało oddalone" },
          ],
        },
        {
          code: "oswiadczenieNauka",
          type: "declaration",
          label:
            "Oświadczam, że w obecnym oraz w kolejnym roku szkolnym dziecko (dzieci) będzie uczyć się w szkole poza miejscem zamieszkania. W przypadku rezygnacji z kontynuacji nauki poza miejscem zamieszkania zobowiązuję się niezwłocznie poinformować o tym podmiot realizujący świadczenia rodzinne.",
          required: true,
          visibleWhen: { any: [dodatek("naukaZamieszkanie"), dodatek("naukaDojazd")] },
        },
      ],
    },
    {
      code: "becikowe",
      title: "Jednorazowa zapomoga z tytułu urodzenia się dziecka",
      visibleWhen: BEC,
      fields: [
        feeInfo(
          "infoBecikowe",
          "Wniosek składa się w terminie 12 miesięcy od dnia narodzin dziecka, a przy opiece prawnej, opiece faktycznej albo przysposobieniu — w terminie 12 miesięcy od objęcia dziecka opieką albo przysposobienia, nie później niż do ukończenia przez dziecko 18. roku życia. Wniosek złożony po terminie pozostawia się bez rozpoznania (art. 15b ust. 3 ustawy).",
        ),
        personsField({ code: "dzieciBecikowe", label: "Dzieci, na które składasz wniosek", itemLabel: "Dziecko", required: true, fields: childFields() }),
        {
          code: "becikoweTytul",
          type: "radio",
          label: "Wobec dziecka jestem",
          required: true,
          options: [
            { value: "rodzic", label: "matką albo ojcem" },
            { value: "opiekun", label: "opiekunem prawnym, opiekunem faktycznym albo osobą, która przysposobiła dziecko" },
          ],
        },
      ],
    },
    {
      code: "zasilekPielegnacyjny",
      title: "Zasiłek pielęgnacyjny",
      visibleWhen: ZP,
      fields: [
        {
          code: "zpDotyczy",
          type: "radio",
          label: "Wniosek dotyczy",
          required: true,
          defaultValue: "wnioskodawca",
          options: [
            { value: "wnioskodawca", label: "mnie" },
            { value: "inna", label: "osoby, której jestem przedstawicielem ustawowym (np. dziecka)" },
          ],
        },
        ...personOfCare("zp", { field: "zpDotyczy", equals: "inna" }),
        {
          code: "zpPodstawa",
          type: "radio",
          label: "Zasiłek przysługuje w związku z",
          required: true,
          options: [
            { value: "orzeczenie", label: "orzeczeniem o niepełnosprawności albo o stopniu niepełnosprawności" },
            { value: "wiek", label: "ukończeniem 75 lat" },
          ],
        },
      ],
    },
    {
      code: "specjalnyZasilek",
      title: "Osoba wymagająca opieki",
      description: "Składam wniosek o specjalny zasiłek opiekuńczy w związku z opieką nad:",
      visibleWhen: SZO,
      fields: [
        ...personOfCare("szo"),
        {
          code: "szoPokrewienstwo",
          type: "text",
          label: "Stopień pokrewieństwa z osobą wymagającą opieki",
          required: true,
          width: "half",
          maxLength: 100,
          placeholder: "np. matka, syn",
        },
      ],
    },
    {
      code: "swiadczeniePielegnacyjne",
      title: "Osoba wymagająca opieki",
      description: "Składam wniosek o ustalenie prawa do świadczenia pielęgnacyjnego w związku z opieką nad:",
      visibleWhen: SP,
      fields: [
        feeInfo(
          "infoSp",
          "Na zasadach obowiązujących od 1 stycznia 2024 r. świadczenie pielęgnacyjne przysługuje w związku z opieką nad osobą w wieku do ukończenia 18. roku życia legitymującą się orzeczeniem o znacznym stopniu niepełnosprawności albo orzeczeniem o niepełnosprawności łącznie ze wskazaniami konieczności stałej lub długotrwałej opieki lub pomocy innej osoby oraz stałego współudziału opiekuna w procesie leczenia, rehabilitacji i edukacji.",
        ),
        ...personOfCare("sp"),
        {
          code: "spRelacja",
          type: "radio",
          label: "Wobec osoby wymagającej opieki jestem",
          required: true,
          options: [
            { value: "rodzic", label: "matką albo ojcem" },
            {
              value: "zobowiazany",
              label: "inną osobą, na której ciąży obowiązek alimentacyjny (np. bratem, siostrą, dziadkiem, babcią), albo małżonkiem",
            },
            { value: "opiekunFaktyczny", label: "opiekunem faktycznym dziecka, który wystąpił do sądu rodzinnego z wnioskiem o przysposobienie" },
            {
              value: "piecza",
              label:
                "rodziną zastępczą, osobą prowadzącą rodzinny dom dziecka albo dyrektorem placówki opiekuńczo-wychowawczej, regionalnej placówki opiekuńczo-terapeutycznej lub interwencyjnego ośrodka preadopcyjnego",
            },
          ],
        },
      ],
    },
    {
      code: "swiadczenieRodzicielskie",
      title: "Świadczenie rodzicielskie",
      visibleWhen: SRD,
      fields: [
        personsField({
          code: "dzieciRodzicielskie",
          label: "Dzieci, na które składasz wniosek o świadczenie rodzicielskie",
          itemLabel: "Dziecko",
          required: true,
          fields: childFields({
            code: "wydluzenie",
            type: "select",
            label: "Wydłużenie okresu świadczenia — dziecko",
            hint: "Tylko gdy wnosisz o wydłużenie z powodu pobytu dziecka w szpitalu po porodzie.",
            options: [
              { value: "przed28", label: "urodziło się przed ukończeniem 28. tygodnia ciąży lub z masą urodzeniową nie większą niż 1000 g" },
              { value: "od28do37", label: "urodziło się po ukończeniu 28. i przed ukończeniem 37. tygodnia ciąży i z masą urodzeniową większą niż 1000 g" },
              {
                value: "po37",
                label:
                  "urodziło się po ukończeniu 37. tygodnia ciąży i przebywało w szpitalu po urodzeniu co najmniej 2 kolejne dni, przy czym pierwszy z tych dni przypadał w okresie od 5. do 28. dnia po porodzie",
              },
            ],
          }),
        }),
        {
          code: "srTytul",
          type: "radio",
          label: "Jestem",
          required: true,
          options: [
            { value: "rodzic", label: "matką albo ojcem dziecka" },
            { value: "opiekunFaktyczny", label: "opiekunem faktycznym dziecka (dziecko w wieku do ukończenia 14. roku życia)" },
            {
              value: "rodzinaZastepcza",
              label: "rodziną zastępczą, z wyjątkiem rodziny zastępczej zawodowej (dziecko w wieku do ukończenia 7. roku życia, a przy odroczonym obowiązku szkolnym — 10. roku życia)",
            },
            { value: "przysposobienie", label: "osobą, która przysposobiła dziecko (dziecko w wieku do ukończenia 14. roku życia)" },
          ],
          hint: "Ojcu dziecka świadczenie przysługuje w przypadku skrócenia przez matkę okresu jego pobierania (po wykorzystaniu co najmniej 14 tygodni od dnia urodzenia dziecka), śmierci matki albo porzucenia przez nią dziecka (art. 17c ust. 2 ustawy).",
        },
        {
          code: "srWydluzenie",
          type: "radio",
          label: "Czy wnosisz o wydłużenie okresu świadczenia z powodu pobytu dziecka w szpitalu po porodzie (art. 17c ust. 3a ustawy)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "srBezrobotna",
          type: "radio",
          label:
            "Czy matka dziecka urodziła je w okresie pobierania zasiłku dla bezrobotnych lub w ciągu miesiąca po jego zakończeniu, albo w okresie przedłużenia pobierania zasiłku dla bezrobotnych?",
          required: true,
          visibleWhen: { field: "srTytul", equals: "rodzic" },
          options: YES_NO,
        },
      ],
    },
    {
      code: "rodzinaDochody",
      title: "Rodzina i dochody",
      visibleWhen: INCOME_TESTED,
      fields: [
        personsField({
          code: "czlonkowieRodziny",
          label: "Członkowie rodziny (oprócz Ciebie)",
          itemLabel: "Członek rodziny",
          requiredWhen: { any: [ZR, BEC] },
          hint: "Małżonek albo drugi rodzic dzieci, z którym wychowujesz co najmniej jedno wspólne dziecko; dzieci do ukończenia 25. roku życia pozostające na utrzymaniu (także te, na które składasz wniosek); dziecko powyżej 25 lat z orzeczeniem o znacznym stopniu niepełnosprawności, jeśli przysługuje na nie świadczenie pielęgnacyjne, specjalny zasiłek opiekuńczy lub zasiłek dla opiekuna. Do rodziny nie zalicza się dziecka pod opieką opiekuna prawnego, dziecka w związku małżeńskim ani pełnoletniego dziecka, które ma własne dziecko. Jeśli rodzinę tworzysz tylko Ty — usuń pustą pozycję.",
        }),
        ...familyIncomeFields({ period: "okres, na który ustalane jest prawo do świadczenia", nonTaxedForm: "ZSR-05", farmForm: "ZSR-07" }),
        {
          code: "alimentyNaDzieci",
          type: "radio",
          label: "Czy na rzecz dziecka (dzieci) zostały zasądzone alimenty od rodzica?",
          required: true,
          visibleWhen: { any: [ZR, BEC] },
          options: YES_NO,
        },
      ],
    },
    {
      code: "zaGranica",
      title: "Pobyt za granicą",
      visibleWhen: { not: BEC },
      fields: abroadFields(),
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswiadczenieZasilekRodzinny",
          type: "declaration",
          visibleWhen: ZR,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do zasiłku rodzinnego; na dziecko (dzieci), na które ubiegam się o zasiłek rodzinny, nie jest pobierany w innej instytucji zasiłek rodzinny ani walutowy dodatek rodzinny; pełnoletnie dziecko (dzieci) nie jest uprawnione do zasiłku rodzinnego na własne dziecko; dziecko (dzieci) nie pozostaje w związku małżeńskim ani nie zostało umieszczone w pieczy zastępczej lub instytucji zapewniającej całodobowe utrzymanie; członkowi rodziny nie przysługuje na dziecko zasiłek rodzinny za granicą, chyba że przepisy o koordynacji systemów zabezpieczenia społecznego lub dwustronne umowy o zabezpieczeniu społecznym stanowią inaczej. W przypadku rezygnacji dziecka z kontynuacji nauki zobowiązuję się niezwłocznie poinformować o tym podmiot wypłacający zasiłek rodzinny wraz z dodatkami.",
        },
        {
          code: "oswiadczenieBecikowe",
          type: "declaration",
          visibleWhen: BEC,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do jednorazowej zapomogi z tytułu urodzenia się dziecka; członkowi rodziny nie przysługuje na dziecko świadczenie z tytułu urodzenia dziecka za granicą.",
        },
        {
          code: "oswiadczenieZasilekPielegnacyjny",
          type: "declaration",
          visibleWhen: ZP,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do zasiłku pielęgnacyjnego; osoba, której wniosek dotyczy, nie przebywa w instytucji zapewniającej całodobowe utrzymanie (dom pomocy społecznej, młodzieżowy ośrodek wychowawczy, schronisko dla nieletnich, zakład poprawczy, areszt śledczy, zakład karny, szkoła wojskowa lub inna szkoła, jeżeli instytucje te zapewniają nieodpłatnie pełne utrzymanie), nie pobiera zasiłku pielęgnacyjnego w innej instytucji i nie jest uprawniona do dodatku pielęgnacyjnego; członkowi rodziny nie przysługuje za granicą świadczenie na pokrycie wydatków związanych z pielęgnacją tej osoby, chyba że przepisy o koordynacji systemów zabezpieczenia społecznego lub dwustronne umowy o zabezpieczeniu społecznym stanowią inaczej.",
        },
        {
          code: "oswiadczenieSzo",
          type: "declaration",
          visibleWhen: SZO,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do specjalnego zasiłku opiekuńczego; nie mam ustalonego prawa do emerytury, renty, renty rodzinnej z tytułu śmierci małżonka przyznanej w przypadku zbiegu prawa do renty rodzinnej i innego świadczenia emerytalno-rentowego, renty socjalnej, zasiłku stałego, nauczycielskiego świadczenia kompensacyjnego, zasiłku przedemerytalnego, świadczenia przedemerytalnego, rodzicielskiego świadczenia uzupełniającego ani świadczenia pieniężnego dla członków rodzin funkcjonariuszy lub żołnierzy zawodowych, których śmierć nastąpiła w związku ze służbą; nie mam ustalonego prawa do świadczenia pielęgnacyjnego, specjalnego zasiłku opiekuńczego i zasiłku dla opiekuna; nie legitymuję się orzeczeniem o znacznym stopniu niepełnosprawności; osoba wymagająca opieki nie została umieszczona w rodzinie zastępczej (z wyjątkiem spokrewnionej), w rodzinnym domu dziecka ani — w związku z koniecznością kształcenia, rewalidacji lub rehabilitacji — w placówce zapewniającej całodobową opiekę (z wyjątkiem podmiotu wykonującego działalność leczniczą), ani nie korzysta w niej z całodobowej opieki przez więcej niż 5 dni w tygodniu; na osobę wymagającą opieki inna osoba nie ma ustalonego prawa do wcześniejszej emerytury ani do dodatku do zasiłku rodzinnego z tytułu urlopu wychowawczego, specjalnego zasiłku opiekuńczego, świadczenia pielęgnacyjnego lub zasiłku dla opiekuna i nie jest uprawniona za granicą do świadczenia na pokrycie wydatków związanych z opieką (chyba że przepisy o koordynacji lub umowy dwustronne stanowią inaczej); nie jestem zatrudniona/zatrudniony ani nie wykonuję innej pracy zarobkowej.",
        },
        {
          code: "oswiadczenieSwiadczeniePielegnacyjne",
          type: "declaration",
          visibleWhen: SP,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do świadczenia pielęgnacyjnego; nie mam ustalonego prawa do świadczenia pielęgnacyjnego, specjalnego zasiłku opiekuńczego ani zasiłku dla opiekuna; osoba wymagająca opieki nie została umieszczona i nie przebywa w domu pomocy społecznej, zakładzie opiekuńczo-leczniczym, zakładzie pielęgnacyjno-opiekuńczym, placówce zapewniającej całodobową opiekę osobom niepełnosprawnym, przewlekle chorym lub w podeszłym wieku, zakładzie karnym, zakładzie poprawczym, areszcie śledczym ani schronisku dla nieletnich; ani osoba wymagająca opieki, ani inna osoba na nią nie jest uprawniona za granicą do świadczenia na pokrycie wydatków związanych z opieką, chyba że przepisy o koordynacji systemów zabezpieczenia społecznego lub dwustronne umowy o zabezpieczeniu społecznym stanowią inaczej.",
        },
        {
          code: "oswiadczenieRodzicielskie",
          type: "declaration",
          visibleWhen: SRD,
          required: true,
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do świadczenia rodzicielskiego; ani ja, ani drugi z rodziców (a w przypadku rodziny zastępczej lub opiekuna faktycznego — ta osoba) nie otrzymujemy zasiłku macierzyńskiego ani uposażenia za okres urlopu macierzyńskiego, urlopu na warunkach urlopu macierzyńskiego, urlopu rodzicielskiego lub uzupełniającego urlopu macierzyńskiego; dziecko nie zostało umieszczone w pieczy zastępczej (nie dotyczy rodziny zastępczej i opiekuna faktycznego); w związku z wychowywaniem tego samego dziecka lub opieką nad nim nie jest ustalone prawo do świadczenia rodzicielskiego, dodatku do zasiłku rodzinnego z tytułu opieki nad dzieckiem w okresie korzystania z urlopu wychowawczego, świadczenia pielęgnacyjnego przyznanego na zasadach obowiązujących do 31 grudnia 2023 r., specjalnego zasiłku opiekuńczego ani zasiłku dla opiekuna; nie przysługuje mi za granicą świadczenie o podobnym charakterze, chyba że przepisy o koordynacji lub umowy dwustronne stanowią inaczej; nie podjęłam/nie podjąłem ani nie kontynuuję zatrudnienia lub innej pracy zarobkowej, która uniemożliwia mi sprawowanie osobistej opieki nad dzieckiem.",
        },
        feeInfo(
          "infoZmiany",
          "Masz obowiązek niezwłocznie powiadomić organ wypłacający świadczenie o zmianach mających wpływ na prawo do niego — m.in. o zmianie liczby członków rodziny, uzyskaniu dochodu i wyjeździe Twoim lub członka rodziny za granicę. Niepoinformowanie może skutkować powstaniem nienależnie pobranych świadczeń i koniecznością ich zwrotu wraz z odsetkami ustawowymi za opóźnienie.",
        ),
        bankAccountField(),
        penaltyDeclaration(),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "kartaPobytu",
      label: "Dokument potwierdzający prawo pobytu cudzoziemca w Polsce (karta pobytu i decyzja o udzieleniu zezwolenia na pobyt)",
      hint: "Kopia (§ 3 rozporządzenia).",
      visibleWhen: FOREIGNER,
      required: true,
    },
    ...familyIncomeAttachments({ nonTaxedForm: "ZSR-05", farmForm: "ZSR-07", when: INCOME_TESTED }),
    {
      code: "orzeczenieAlimenty",
      label: "Odpis wykonalnego orzeczenia sądu albo ugody zasądzającej alimenty na rzecz dziecka",
      visibleWhen: CHILD_SUPPORT,
      required: true,
      multiple: true,
    },
    {
      code: "zaswiadczenieKomornika",
      label: "Zaświadczenie komornika o bezskuteczności egzekucji alimentów i o kwocie wyegzekwowanej",
      hint: "Nieobowiązkowo — gdy alimenty nie są płacone w pełnej wysokości (oryginał); przy egzekucji za granicą — informacja sądu o podjętych czynnościach.",
      visibleWhen: CHILD_SUPPORT,
      multiple: true,
    },
    {
      code: "zaswiadczenieOpiekaMedyczna",
      label: "Zaświadczenie lekarskie lub wystawione przez położną o pozostawaniu kobiety pod opieką medyczną nie później niż od 10. tygodnia ciąży do porodu",
      visibleWhen: { any: [{ all: [BEC, { field: "becikoweTytul", equals: "rodzic" }] }, dodatek("urodzenie")] },
      required: true,
    },
    {
      code: "oswiadczenieUrlopWychowawczy",
      label: "Oświadczenie o terminie i okresie urlopu wychowawczego oraz o co najmniej 6-miesięcznym zatrudnieniu przed jego uzyskaniem (ZSR-08)",
      visibleWhen: dodatek("urlop"),
      required: true,
    },
    {
      code: "oswiadczenieZameldowanie",
      label: "Oświadczenie potwierdzające tymczasowe zameldowanie ucznia poza miejscem zamieszkania (ZSR-12)",
      visibleWhen: dodatek("naukaZamieszkanie"),
      required: true,
    },
    {
      code: "orzeczenie",
      label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności",
      hint: "Dziecka (dodatek z tytułu kształcenia i rehabilitacji) albo osoby, której dotyczy wniosek lub nad którą sprawujesz opiekę — kopia.",
      visibleWhen: { any: [dodatek("niepelnosprawnosc"), { all: [ZP, { field: "zpPodstawa", equals: "orzeczenie" }] }, SZO, SP] },
      required: true,
      multiple: true,
    },
    {
      code: "zaswiadczeniePrzysposobienie",
      label: "Zaświadczenie sądu rodzinnego lub ośrodka adopcyjnego o prowadzonym postępowaniu w sprawie o przysposobienie dziecka",
      visibleWhen: { all: [SP, { field: "spRelacja", equals: "opiekunFaktyczny" }] },
      required: true,
    },
    {
      code: "zaswiadczeniePiecza",
      label: "Zaświadczenie o umieszczeniu dziecka w rodzinie zastępczej, rodzinnym domu dziecka albo placówce",
      hint: "Wydane przez organizatora rodzinnej pieczy zastępczej, powiatowe centrum pomocy rodzinie albo podmiot z art. 190 ustawy o wspieraniu rodziny; dyrektor placówki lub ośrodka dołącza także dokument potwierdzający pełnienie funkcji.",
      visibleWhen: { all: [SP, { field: "spRelacja", equals: "piecza" }] },
      required: true,
      multiple: true,
    },
    {
      code: "postanowienieOpieka",
      label: "Postanowienie sądu o przysposobieniu dziecka, o objęciu go opieką albo o umieszczeniu w rodzinie zastępczej",
      hint: "Kopia.",
      visibleWhen: { all: [SRD, { field: "srTytul", in: ["opiekunFaktyczny", "rodzinaZastepcza", "przysposobienie"] }] },
      required: true,
    },
    {
      code: "zaswiadczenieSzpital",
      label: "Zaświadczenie ze szpitala o okresie pobytu dziecka w szpitalu oraz o tygodniu ciąży i masie urodzeniowej przy urodzeniu",
      hint: "Art. 17c ust. 3b ustawy.",
      visibleWhen: { all: [SRD, { field: "srWydluzenie", equals: "tak" }] },
      required: true,
      multiple: true,
    },
    socialProxyAttachment(),
    otherAttachments(),
  ],
};

export default C6;
